import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, provide, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';

import {
  NavBar,
  LoaderIndicator,
  HelperViewStatus
} from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons';
import { FadeTransition } from '@/modules/common/components/FadeTransition';

import {
  getInvestmentInfo,
  getInvestmentReview,
  makeInvestment,
  IInvestmentInfo,
  IInvestmentReview,
  IInvestmentConclusion
} from '@/modules/transaction/services/fund-pension-investment';
import {
  getPendingRiskSignature,
  getPendingTermSignatureById,
  IRiskSignature,
  ITermSignature
} from '@/modules/transaction/services/product-investment';
import { isBFFError } from '@/modules/common/helpers/bff';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';

import {
  FundPensionInvestmentComponents,
  EFundPensionInvestmentSteps
} from './helpers';


export default /*#__PURE__*/_defineComponent({
  __name: 'FundPensionInvestment',
  setup(__props) {

const route = useRoute();
const router = useRouter();

const currentStep = ref(EFundPensionInvestmentSteps.INVESTMENT);
const investmentInfo = ref<IInvestmentInfo>();
const investmentReview = ref<IInvestmentReview>();
const investmentConclusion = ref<IInvestmentConclusion>();

const isLoading = ref(true);
const hasError = ref(false);
const amount = ref(0);

const riskSignature = ref<IRiskSignature>();
const termSignature = ref<ITermSignature>();

const step = computed(() => {
  return {
    component: FundPensionInvestmentComponents[currentStep.value]
  };
});

const showNavbar = computed(() => {
  return currentStep.value !== EFundPensionInvestmentSteps.CONCLUSION;
});

const routerParams = computed(() => {
  const { productId, portfolioId } = route.params;

  return { productId, portfolioId };
});

function onBack() {
  if (currentStep.value === EFundPensionInvestmentSteps.INVESTMENT)
    return onClose();

  currentStep.value = currentStep.value - 1;
}

function onClose() {
  if (window.history.length > 1) {
    window.history.back();
  } else {
    router.push({
      name: 'productsCatalog',
      params: {
        type: route.params.type || 'fixed-income'
      }
    });
  }
}

async function getDetails() {
  try {
    isLoading.value = true;
    hasError.value = false;

    await Promise.all([
      fetchInvestmentInfo(),
      fetchRiskSignature(),
      fetchTermSignature()
    ]);
  } catch (error) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

async function fetchInvestmentInfo() {
  const { productId, portfolioId } = routerParams.value;

  investmentInfo.value = await getInvestmentInfo(productId, portfolioId);
}

async function fetchRiskSignature() {
  riskSignature.value = await getPendingRiskSignature(
    routerParams.value.productId,
    'fund-pension'
  );
}

async function fetchTermSignature() {
  termSignature.value = await getPendingTermSignatureById(
    routerParams.value.productId,
    'fund-pension'
  );
}

async function fetchInvestmentReview(amountValue: number) {
  amount.value = amountValue;

  try {
    const { productId, portfolioId } = routerParams.value;

    investmentReview.value = await getInvestmentReview(
      productId,
      portfolioId,
      amount.value
    );

    currentStep.value = EFundPensionInvestmentSteps.REVIEW;
  } catch (e) {
    let errorMessage =
      'Não foi possível realizar a operação, por favor tente novamente.';

    if (isBFFError(e)) {
      errorMessage = e.error.message.formatted;
    }

    notify({
      group: 'wrn-notifications',
      text: errorMessage,
      type: NotificationTypes.error
    });
  }
}

async function fetchInvestment() {
  try {
    const { productId, portfolioId } = routerParams.value;

    investmentConclusion.value = await makeInvestment(
      productId,
      portfolioId,
      amount.value
    );

    currentStep.value = EFundPensionInvestmentSteps.CONCLUSION;
  } catch (e) {
    let errorMessage =
      'Não foi possível realizar a operação, por favor tente novamente.';

    if (isBFFError(e)) {
      errorMessage = e.error.message.formatted;
    }

    notify({
      group: 'wrn-notifications',
      text: errorMessage,
      type: NotificationTypes.error
    });
  }
}

onMounted(getDetails);

provide('productId', routerParams.value.productId);
provide('investmentInfo', investmentInfo);
provide('investmentReview', investmentReview);
provide('investmentConclusion', investmentConclusion);
provide('riskSignatureData', riskSignature);
provide('termSignatureData', termSignature);

provide('fetchInvestmentReview', fetchInvestmentReview);
provide('fetchInvestment', fetchInvestment);

return { __sfc: true,route, router, currentStep, investmentInfo, investmentReview, investmentConclusion, isLoading, hasError, amount, riskSignature, termSignature, step, showNavbar, routerParams, onBack, onClose, getDetails, fetchInvestmentInfo, fetchRiskSignature, fetchTermSignature, fetchInvestmentReview, fetchInvestment, NavBar, LoaderIndicator, HelperViewStatus, BackButton, FadeTransition }
}

})